import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { LocationOn, Star, TrendingUp } from '@mui/icons-material';

const Destinations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);

  const destinations = [
    {
      id: 1,
      name: 'United States',
      image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      description: 'The United States offers world-renowned universities and colleges with cutting-edge research facilities and diverse academic programs.',
      features: ['Ivy League Universities', 'Research Opportunities', 'Global Recognition'],
      rating: 4.9,
      students: '1M+',
      color: '#2C5AA0',
    },
    {
      id: 2,
      name: 'Australia',
      image: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      description: 'Australia is one of the most desired destinations for education among international students. Each academic year, more than 200,000 foreign students choose to study in Australia.',
      features: ['Top Universities', 'Work Rights', 'PR Pathway'],
      rating: 4.8,
      students: '200K+',
      color: '#FF6B35',
    },
    {
      id: 3,
      name: 'Canada',
      image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      description: 'Canada is a top education destination for its world-class universities and colleges, post-study work and migration options on offer.',
      features: ['Quality Education', 'Immigration Friendly', 'Safe Environment'],
      rating: 4.9,
      students: '150K+',
      color: '#10B981',
    },
    {
      id: 4,
      name: 'United Kingdom',
      image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      description: 'The benefits of studying in the UK go beyond the classroom to not only get qualifications that are respected by employers worldwide.',
      features: ['Prestigious Universities', 'Global Recognition', 'Rich Culture'],
      rating: 4.7,
      students: '180K+',
      color: '#8B5CF6',
    },
    {
      id: 5,
      name: 'New Zealand',
      image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      description: 'New Zealand offers world-class education with a safe, welcoming environment and excellent post-study work opportunities.',
      features: ['Safe Environment', 'Work Rights', 'Beautiful Country'],
      rating: 4.8,
      students: '50K+',
      color: '#10B981',
    },
    {
      id: 6,
      name: 'Ireland',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      description: 'Ireland provides excellent education opportunities with strong ties to Europe and a growing tech industry.',
      features: ['EU Access', 'Tech Hub', 'English Speaking'],
      rating: 4.6,
      students: '35K+',
      color: '#059669',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <Box
      id="destinations"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              color: 'text.primary',
              fontWeight: 700,
            }}
          >
            Featured Destinations
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Discover the world's top study destinations with our expert guidance
          </Typography>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={4}>
            {destinations.map((destination, index) => (
              <Grid item xs={12} md={4} key={destination.id}>
                <motion.div variants={cardVariants}>
                  <Card
                    onMouseEnter={() => setHoveredCard(destination.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: theme.shadows[2],
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[6],
                        background: 'rgba(255, 255, 255, 0.95)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', height: 280 }}>
                      <CardMedia
                        component="img"
                        height="280"
                        image={destination.image}
                        alt={destination.name}
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/800x600/2c5aa0/ffffff?text=${destination.name}`;
                        }}
                        sx={{
                          transition: 'transform 0.4s ease',
                          transform: hoveredCard === destination.id ? 'scale(1.05)' : 'scale(1)',
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${destination.color}20 0%, ${destination.color}40 100%)`,
                          opacity: hoveredCard === destination.id ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                        }}
                      >
                        <Star sx={{ fontSize: '1rem', color: '#FFD700' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {destination.rating}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: 16,
                          right: 16,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            color: 'white',
                            fontWeight: 700,
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          }}
                        >
                          {destination.name}
                        </Typography>
                        <Chip
                          label={`${destination.students} students`}
                          size="small"
                          sx={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            color: 'text.primary',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.7,
                          mb: 3,
                          flexGrow: 1,
                        }}
                      >
                        {destination.description}
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, minHeight: '60px' }}>
                        {destination.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              background: `${destination.color}15`,
                              color: destination.color,
                              fontWeight: 500,
                              border: `1px solid ${destination.color}30`,
                            }}
                          />
                        ))}
                      </Box>

                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{
                            background: `linear-gradient(135deg, ${destination.color} 0%, ${destination.color}CC 100%)`,
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1rem',
                            boxShadow: theme.shadows[2],
                            '&:hover': {
                              boxShadow: theme.shadows[4],
                            },
                          }}
                        >
                          Explore {destination.name}
                        </Button>
                      </motion.div>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Destinations;
